import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../../../theme/Colors';

export type BadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
export type BadgeSize = 'small' | 'medium' | 'large';

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: React.ReactNode;
  dot?: boolean;
  rounded?: boolean;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  dot = false,
  rounded = false,
  className,
}) => {
  return (
    <StyledBadge
      $variant={variant}
      $size={size}
      $rounded={rounded}
      className={className}
    >
      {dot && <BadgeDot $variant={variant} />}
      {children}
    </StyledBadge>
  );
};

// Color configurations
const variantColors = {
  primary: {
    background: 'rgba(146, 130, 203, 0.1)',
    color: COLORS.primary,
    dotColor: COLORS.primary,
  },
  secondary: {
    background: 'rgba(107, 114, 128, 0.1)',
    color: '#6B7280',
    dotColor: '#6B7280',
  },
  success: {
    background: 'rgba(16, 185, 129, 0.1)',
    color: '#10B981',
    dotColor: '#10B981',
  },
  warning: {
    background: 'rgba(245, 158, 11, 0.1)',
    color: '#F59E0B',
    dotColor: '#F59E0B',
  },
  danger: {
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#EF4444',
    dotColor: '#EF4444',
  },
  info: {
    background: 'rgba(59, 130, 246, 0.1)',
    color: '#3B82F6',
    dotColor: '#3B82F6',
  },
};

const sizeStyles = {
  small: css`
    padding: 2px 8px;
    font-size: 12px;
    font-weight: 500;
  `,
  medium: css`
    padding: 4px 12px;
    font-size: 14px;
    font-weight: 600;
  `,
  large: css`
    padding: 6px 16px;
    font-size: 16px;
    font-weight: 600;
  `,
};

const StyledBadge = styled.span<{
  $variant: BadgeVariant;
  $size: BadgeSize;
  $rounded: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: ${({ $rounded }) => $rounded ? '9999px' : '6px'};
  line-height: 1;
  white-space: nowrap;
  
  ${({ $size }) => sizeStyles[$size]}
  background-color: ${({ $variant }) => variantColors[$variant].background};
  color: ${({ $variant }) => variantColors[$variant].color};
`;

const BadgeDot = styled.span<{ $variant: BadgeVariant }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ $variant }) => variantColors[$variant].dotColor};
`;

export default Badge;